import React, { useState, useRef } from "react";
import { Button, Card, CardBody, CardHeader, Divider, Select, SelectItem, Textarea, Input } from "@nextui-org/react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import { postFetcher } from "../../../../services/api";
import { hasValidRole } from "../../../../services/helpers";
import useAuth from "../../../../hooks/useAuth";
import RolesAccess from "../../../../enums/roles";
import StatusPermohonanRegistrasi from "../../../../enums/statusRegistrasi";
import { postMultipartFetcher } from "../../../../services/api";

const formSchema = yup.object().shape({
  aksi: yup.string().required("Aksi wajib dipilih"),
  keterangan: yup.string().required("Keterangan wajib diisi"),
  tanggal_sk_insw: yup.date().typeError("Tanggal harus valid").required("Tanggal SK INSW wajib diisi"),
  file_sk_insw: yup.mixed().required("File SK INSW wajib diunggah"),
});

export default function UpdateINSW({ id, mutate, data }) {
  const { user, roles } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const aksiOptions = [
    { key: "1", label: "1. Perubahan Profil" },
    { key: "2", label: "2. Perubahan Komoditas" },
    { key: "3", label: "3. Perubahan Profil dan Komoditas" },
    { key: "4", label: "4. Pembekuan" },
    { key: "5", label: "5. Pengaktifan Pembekuan" },
    { key: "6", label: "6. Pencabutan" },
  ];

  const getJnsPengajuanAndKdKeputusan = (selectedOption) => {
    switch (selectedOption) {
      case "1":
        return { jnsPengajuan: "0000100000", kdKeputusan: "008" };
      case "2":
        return { jnsPengajuan: "0010000000", kdKeputusan: "009" };
      case "3":
        return { jnsPengajuan: "0010100000", kdKeputusan: "001" };
      case "4":
        return { jnsPengajuan: "0001000000", kdKeputusan: "001" };
      case "5":
        return { jnsPengajuan: "0000000100", kdKeputusan: "001" };
      case "6":
        return { jnsPengajuan: "0000000010", kdKeputusan: "001" };
      default:
        return { jnsPengajuan: "", kdKeputusan: "" };
    }
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      aksi: "",
      keterangan: "",
      tanggal_sk_insw: "",
      file_sk_insw: null,
    },
  });

  const watchedFile = watch("file_sk_insw");

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const { jnsPengajuan, kdKeputusan } = getJnsPengajuanAndKdKeputusan(formData.aksi);

      // Buat FormData object
      const submitFormData = new FormData();

      // Append data yang diperlukan
      submitFormData.append("id", id);
      submitFormData.append("jnsPengajuan", jnsPengajuan);
      submitFormData.append("kdKeputusan", kdKeputusan);
      submitFormData.append("keterangan", formData.keterangan);
      submitFormData.append("status", "riwayat");
      submitFormData.append("approval_status", "selesai");

      // Append tanggal jika ada
      if (formData.tanggal_sk_insw) {
        const tanggalISO = new Date(formData.tanggal_sk_insw).toISOString();
        submitFormData.append("tanggal_sk_insw", tanggalISO);
      }

      // Append file jika ada
      if (formData.file_sk_insw && formData.file_sk_insw.length > 0) {
        submitFormData.append("file_sk_insw", formData.file_sk_insw[0]);
      }

      await postMultipartFetcher(`/api/insw/update`, submitFormData);
      mutate();
      reset();
      toast.success("Update INSW berhasil!");
    } catch (error) {
      console.error("Error updating INSW:", error);
      toast.error("Gagal update INSW: " + (error?.response?.data?.message?.message || error?.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validasi file PDF
      if (file.type !== "application/pdf") {
        toast.error("File harus berformat PDF");
        return;
      }

      // Validasi ukuran file (misal max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Ukuran file maksimal 10MB");
        return;
      }

      setValue("file_sk_insw", event.target.files, { shouldDirty: true });
    }
  };

  const removeFile = () => {
    setValue("file_sk_insw", null, { shouldDirty: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const hasUnsavedChanges = Object.keys(dirtyFields)?.length > 0;

  // Check if user has permission to update INSW - hanya bisa update kalau status "Selesai"
  const canUpdate = hasValidRole(roles, [RolesAccess.PIC_REGISTRASI, RolesAccess.SUPER_ADMIN, RolesAccess.DIREKTUR]) && data?.status === StatusPermohonanRegistrasi.SELESAI;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <Card radius="sm" className="h-full">
        <CardHeader className="flex items-center justify-between">
          <div>
            <p className="text-md">Update INSW</p>
            {hasUnsavedChanges && <span className="text-red-500 text-sm ml-2">Perubahan belum disimpan</span>}
          </div>
        </CardHeader>

        <Divider />

        <CardBody>
          <div className="grid grid-cols-1 gap-4 mb-5">
            {/* Pilih Aksi - existing code */}
            <Controller
              name="aksi"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  variant="faded"
                  label="Pilih Aksi"
                  placeholder="Pilih aksi yang akan dilakukan"
                  labelPlacement="outside"
                  selectedKeys={field.value ? [field.value] : []}
                  onSelectionChange={(keys) => {
                    const selectedValue = Array.from(keys)[0];
                    field.onChange(selectedValue);
                  }}
                  color={errors.aksi ? "danger" : "default"}
                  isInvalid={!!errors.aksi}
                  errorMessage={errors.aksi?.message}
                  isDisabled={!canUpdate}
                  isRequired
                >
                  {aksiOptions.map((item) => (
                    <SelectItem key={item.key} value={item.key}>
                      {item.label}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />

            {/* Tanggal SK INSW */}
            <Input
              {...register("tanggal_sk_insw")}
              variant="faded"
              label="Tanggal SK INSW"
              type="date"
              labelPlacement="outside"
              color={errors.tanggal_sk_insw ? "danger" : "default"}
              isInvalid={!!errors.tanggal_sk_insw}
              errorMessage={errors.tanggal_sk_insw?.message}
              isDisabled={!canUpdate}
              isRequired
            />

            {/* File Upload SK INSW */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">File SK INSW (PDF)</label>

              {/* Hidden file input */}
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />

              {/* File display area */}
              <div className="border-2 border-dashed border-line-strong rounded-lg p-4">
                {watchedFile && watchedFile.length > 0 ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-ink-500">📄 {watchedFile[0].name}</span>
                      <span className="text-xs text-ink-400">({(watchedFile[0].size / 1024 / 1024).toFixed(2)} MB)</span>
                    </div>
                    <Button size="sm" color="danger" variant="light" onPress={removeFile} isDisabled={!canUpdate}>
                      Hapus
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-ink-500 mb-2">Klik untuk memilih file PDF</p>
                    <Button size="sm" variant="bordered" onPress={handleFileSelect} isDisabled={!canUpdate}>
                      Pilih File
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Keterangan - existing code */}
            <Textarea
              {...register("keterangan")}
              variant="faded"
              label="Keterangan"
              placeholder="Masukkan keterangan di sini..."
              labelPlacement="outside"
              color={errors.keterangan ? "danger" : "default"}
              isInvalid={!!errors.keterangan}
              errorMessage={errors.keterangan?.message}
              isDisabled={!canUpdate}
              isRequired
              minRows={4}
              maxRows={6}
            />
          </div>

          {/* Action Buttons - existing code */}
          <div className="flex gap-2">
            <Button type="submit" color="primary" isDisabled={isSubmitting || !hasUnsavedChanges || !canUpdate} isLoading={isSubmitting}>
              Jalankan Aksi
            </Button>

            <Button type="button" variant="bordered" onPress={() => reset()} isDisabled={isSubmitting || !hasUnsavedChanges}>
              Reset
            </Button>
          </div>

          {/* Warning message - existing code */}
          {!canUpdate && (
            <div className="mt-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
              <p className="text-warning-600 text-sm">{data?.status !== StatusPermohonanRegistrasi.SELESAI ? "Update INSW hanya dapat dilakukan pada status 'Selesai'." : "Anda tidak memiliki izin untuk melakukan update INSW."}</p>
            </div>
          )}
        </CardBody>
      </Card>
    </form>
  );
}
